import { useT } from '@/core/I18nProvider';

import { OUTCOME_TONE, ROW_LEAD } from './chat-script';

import type { Exchange } from './chat-script';
import type { ReactElement } from 'react';

/**
 * The answer half of a how-it-works exchange — the receipt, the chart, the
 * numbered plan, the refusal.
 *
 * Hoisted out of `ChatSurface.tsx` at the seam that file had already drawn for
 * it (it had crossed the ~300-line ceiling in `.claude/rules/architecture.md`).
 * A leaf: it takes one `Exchange` and imports nothing back from its parent, so
 * the parent is left as the conversation's flow and this is left as what a
 * single answer looks like — which is the split the two were already making
 * informally.
 */

export function ResultCard({ exchange }: { readonly exchange: Exchange }): ReactElement {
  const { t } = useT();
  const [chipBg, chipInk] = OUTCOME_TONE[exchange.outcome];

  return (
    <>
      <div
        className="w-full overflow-hidden rounded-[13px] border border-[#e0e0e6] bg-white"
        style={{
          boxShadow: '0 1px 2px rgba(12,13,16,.04), 0 10px 22px -14px rgba(12,13,16,.26)',
          animation: 'sxUp .44s cubic-bezier(.16,1,.3,1) .12s backwards',
        }}
      >
        <div className="flex items-center gap-2 border-b border-[#f2f2f5] bg-[#fcfcfd] px-3 py-2.5">
          <span
            aria-hidden="true"
            className="size-[7px] flex-none rounded-sm"
            style={{ background: exchange.accent }}
          />
          <span className="min-w-0 flex-1 truncate text-xs font-semibold tracking-[-0.02em] text-[#1a1a20]">
            {t(exchange.cardTitleKey)}
          </span>
          <span className="flex-none whitespace-nowrap font-mono rtl:font-sans text-[9.5px] uppercase tracking-[0.09em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal">
            {t(exchange.cardMetaKey)}
          </span>
        </div>

        {exchange.big ? (
          <div className="flex items-baseline gap-2.5 px-3.5 pb-2.5 pt-3.5">
            <span className="text-[26px] font-semibold leading-none tracking-[-0.042em] text-[color:var(--bd-ink)]">
              {/* Same rule as the row values below: isolated even though a
                  currency figure happens to survive today, because whether it
                  does depends on nobody using a letter in it. */}
              <bdi dir="ltr">{exchange.big}</bdi>
            </span>
            {exchange.bigMetaKey ? (
              <span className="text-[11.5px] text-[color:var(--bd-faint)]">
                {t(exchange.bigMetaKey)}
              </span>
            ) : null}
          </div>
        ) : null}

        {exchange.bars ? (
          <div className="flex flex-col gap-2.5 p-3.5">
            {exchange.bars.map((bar) => (
              <div key={bar.label} className="flex items-center gap-2.5">
                <span className="w-8 flex-none font-mono rtl:font-sans text-[9.5px] uppercase tracking-[0.06em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal">
                  {bar.label}
                </span>
                <span className="h-2 flex-1 overflow-hidden rounded-full bg-[#f2f2f5]">
                  <span
                    className="block h-full rounded-full"
                    style={{
                      width: bar.width,
                      background: bar.hot
                        ? 'linear-gradient(90deg,var(--bd-cyan),var(--bd-blue) 55%,var(--bd-violet))'
                        : 'linear-gradient(90deg,#d6d8e0,#c4c7d2)',
                    }}
                  />
                </span>
                <span
                  className={[
                    'w-14 flex-none text-end text-[11.5px]',
                    bar.hot
                      ? 'font-semibold text-[color:var(--bd-ink)]'
                      : 'text-[color:var(--bd-faint)]',
                  ].join(' ')}
                >
                  {bar.value}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {exchange.rows ? (
          <div className="flex flex-col">
            {exchange.rows.map((row) => {
              const [bg, ink, glyph] = ROW_LEAD[row.kind];
              return (
                <div
                  key={row.labelKey}
                  className="flex items-center gap-2.5 border-t border-[#f4f4f7] px-3.5 py-2"
                >
                  <span
                    aria-hidden="true"
                    className="grid size-[17px] flex-none place-items-center rounded-md font-mono text-[8.5px] font-medium"
                    style={{ background: bg, color: ink }}
                  >
                    {row.lead ?? glyph}
                  </span>
                  <span className="min-w-0 flex-1 text-[11.8px] leading-snug text-[#3d3d47]">
                    {t(row.labelKey)}
                  </span>
                  {/* `valueKey` is a translated WORD, `value` a locale-neutral
                      figure — so the letter-spacing drops for RTL rather than
                      claiming an exemption: this span can now hold Arabic. */}
                  {row.valueKey !== undefined || row.value ? (
                    <span className="flex-none whitespace-nowrap font-mono rtl:font-sans text-[9.5px] tracking-[0.06em] text-[color:var(--bd-faint)] rtl:tracking-normal">
                      {row.valueKey !== undefined ? (
                        t(row.valueKey)
                      ) : (
                        // A neutral figure has no strong direction, so inside an
                        // RTL card the bidi algorithm reorders it: `3–5d` renders
                        // as `5d–3`. `dir="ltr"` rather than a bare `<bdi>`
                        // because `dir="auto"` still guesses from the first
                        // strong character, and `12:04` has none.
                        <bdi dir="ltr">{row.value}</bdi>
                      )}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {exchange.buttons ? (
          <div className="flex items-center gap-2 border-t border-[#f2f2f5] bg-[#fcfcfd] px-3.5 py-2.5">
            {exchange.buttons.map((button) => (
              <span
                key={button.labelKey}
                className={[
                  'rounded-lg border px-3 py-1.5 text-[11.5px] font-medium',
                  button.primary
                    ? 'border-[color:var(--bd-ink)] bg-[color:var(--bd-ink)] text-white'
                    : 'border-[color:var(--bd-line-3)] bg-white text-[#3d3d47]',
                ].join(' ')}
              >
                {t(button.labelKey)}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {exchange.sources ? (
        <div
          className="flex flex-wrap gap-1.5"
          style={{ animation: 'sxUp .4s ease .2s backwards' }}
        >
          {exchange.sources.map((source) => (
            <span
              key={source}
              className="inline-flex items-center gap-1.5 rounded-md border border-[#ececef] bg-[#f6f6f8] px-2 py-1 font-mono text-[9.5px] text-[color:var(--bd-subtle)]"
            >
              <span
                aria-hidden="true"
                className="size-1 rounded-full bg-[color:var(--bd-violet)]"
              />
              {source}
            </span>
          ))}
        </div>
      ) : null}

      <span
        className="rounded-full px-2 py-1 font-mono rtl:font-sans text-[9.5px] uppercase tracking-[0.09em] rtl:normal-case rtl:tracking-normal"
        style={{ background: chipBg, color: chipInk, animation: 'sxUp .4s ease .18s backwards' }}
      >
        {t(exchange.chipKey)}
      </span>
    </>
  );
}
